/**
 * Video Call Context
 * Provides global state management for video calls
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import notificationService from '../services/NotificationService';
import { toast } from 'sonner';

// Call status constants
export const CallStatus = {
  IDLE: 'idle',
  INITIATING: 'initiating',
  CALLING: 'calling',
  RINGING: 'ringing',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  VOICEMAIL: 'voicemail',
  ENDED: 'ended',
  MISSED: 'missed',
  DECLINED: 'declined',
  FAILED: 'failed'
};

// Provider types
export const CallProviderType = {
  ACS_TEAMS: 'acs_teams',
  NATIVE_WEBRTC: 'native_webrtc'
};

const VideoCallContext = createContext(null);

export const useVideoCall = () => {
  const context = useContext(VideoCallContext);
  if (!context) {
    throw new Error('useVideoCall must be used within VideoCallProvider');
  }
  return context;
};

export const VideoCallProvider = ({ children }) => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Call state
  const [callState, setCallState] = useState({
    status: CallStatus.IDLE,
    callId: null,
    providerType: null,
    participants: [],
    isCaller: false,
    isMinimized: false,
    duration: 0,
    callMode: 'video', // 'video' | 'voice'
    isOnHold: false,
  });
  
  // Media state
  const [mediaState, setMediaState] = useState({
    isMicOn: true,
    isCameraOn: true,
    localStream: null,
    remoteStream: null,
    // Secondary remote stream — populated when a 3-way conference is active.
    // The "primary" remote stream above is participant B; this one is C.
    secondaryRemoteStream: null,
  });
  
  // Incoming call state
  const [incomingCall, setIncomingCall] = useState(null);
  
  // WebRTC refs
  const peerConnectionRef = useRef(null);
  // Reference to the latest callState for use inside async callbacks (avoids
  // stale closure when callbacks are stored in refs and invoked asynchronously).
  const callStateRef = useRef(null);
  // Secondary peer connection for 3-way conference. A acts as the audio mixer
  // hub: peerConnectionRef = A↔B, secondaryPeerConnectionRef = A↔C. On Merge,
  // a Web Audio mixer rebroadcasts B's voice to C and C's voice to B so all
  // three hear each other through A. (No SFU required.)
  const secondaryPeerConnectionRef = useRef(null);
  const secondaryCallIdRef = useRef(null);
  const secondaryIcePollingRef = useRef(null);
  const secondaryAnswerPollingRef = useRef(null);
  // Audio mixer state — created lazily when conference is engaged.
  // { ctx, micSrc, primarySrc, secondarySrc, destForPrimary, destForSecondary,
  //   primaryAudioSender, secondaryAudioSender, originalMicTrack }
  const audioMixerRef = useRef(null);
  // Lazily-created silent audio track. We replace the primary peer's audio
  // sender with this whenever B should NOT hear A (hold, briefing, etc.) —
  // doing it via replaceTrack() instead of `track.enabled = false` so that
  // the secondary peer (which shares the same source mic track) is unaffected.
  const silentAudioTrackRef = useRef(null);
  const silentAudioCtxRef = useRef(null);

  const ensureSilentTrack = () => {
    if (silentAudioTrackRef.current && silentAudioTrackRef.current.readyState !== 'ended') {
      return silentAudioTrackRef.current;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!silentAudioCtxRef.current) {
      silentAudioCtxRef.current = new Ctx();
    }
    const ctx = silentAudioCtxRef.current;
    const dest = ctx.createMediaStreamDestination();
    // Nothing connected → silence. Track stays alive as long as the AudioContext does.
    silentAudioTrackRef.current = dest.stream.getAudioTracks()[0];
    return silentAudioTrackRef.current;
  };

  // Helper: swap a peer connection's audio sender to a given track.
  // Used to: (a) silence A→B during hold/briefing, (b) restore A→B's original
  // mic on unhold/cancel, (c) replace with mixed track during merge.
  const replaceAudioSenderTrack = async (pc, newTrack) => {
    if (!pc) return;
    const senders = pc.getSenders().filter((s) => s.track && s.track.kind === 'audio');
    for (const s of senders) {
      try { await s.replaceTrack(newTrack); } catch (e) { console.error('replaceTrack failed:', e); }
    }
  };
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  // Secondary remote video element (renders C in conference mode).
  const secondaryRemoteVideoRef = useRef(null);
  // Hold-music + recording refs (declared up front so callbacks can use them)
  const holdAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  // Forward refs for hold-music helpers (so handleWebSocketMessage can call them
  // without creating a TDZ on the const declarations below)
  const playHoldMusicRef = useRef(null);
  const stopHoldMusicRef = useRef(null);
  // Forward refs for transfer/renegotiation helpers
  const handleRenegotiateOfferRef = useRef(null);
  const handleRenegotiateAnswerRef = useRef(null);
  const handleTransferRedirectRef = useRef(null);
  // Forward ref for teardownSecondary so the WS handler (closure-captured
  // before teardownSecondary is defined later in the file) can reach it.
  const teardownSecondaryRef = useRef(null);
  // Forward refs for placeSecondaryCall + mergeIntoConference so callbacks
  // declared earlier in the file (transferCall, completeWarmTransfer) can
  // invoke them without TDZ.
  const placeSecondaryCallRef = useRef(null);
  const mergeIntoConferenceRef = useRef(null);
  // Ringback tone (caller-side) + 45s timeout + voicemail flow
  const ringbackCtxRef = useRef(null);
  const ringbackNodesRef = useRef(null);
  const ringTimeoutRef = useRef(null);
  const transferAudioRef = useRef(null);
  // Hold-music audio element pre-unlocked during a user gesture (answer/initiate)
  // so the browser autoplay policy doesn't block playback when 'hold' arrives
  // via WebSocket (which is NOT a user-gesture context).
  const primedHoldAudioRef = useRef(null);
  const voicemailStopRef = useRef(null);
  const wsRef = useRef(null);
  const durationIntervalRef = useRef(null);
  
  // ICE servers — fetched from backend (Twilio NTS when configured, public
  // STUN+TURN fallback otherwise). We refresh on each call setup.
  const iceServersRef = useRef([
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]);

  const refreshIceServers = useCallback(async () => {
    try {
      const res = await api.get('/video-calls/ice-servers');
      const list = res?.data?.ice_servers;
      if (Array.isArray(list) && list.length > 0) {
        iceServersRef.current = list;
        return list;
      }
    } catch (e) { /* keep stale list */ }
    return iceServersRef.current;
  }, [api]);

  // Pre-warm on mount so the first call has fresh creds.
  useEffect(() => { refreshIceServers(); }, [refreshIceServers]);

  // For all setupPeerConnection helpers below — they read iceServersRef.current.

  // Navigate to video calls tab
  const navigateToVideoCallsTab = useCallback(() => {
    if (!location.pathname.includes('/calls')) {
      navigate('/calls?tab=video');
    }
  }, [navigate, location.pathname]);

  // ============== WebSocket Connection ==============
  
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const token = localStorage.getItem('orb_token');
    if (!token) return;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/video-call/${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Video call WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('Video call WebSocket disconnected');
        // Attempt reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (event) => {
        // Suppress the noisy 'Event' object that fires on every
        // initial connect failure — `onclose` already triggers a
        // reconnect every 5s, and the error event itself carries no
        // useful diagnostic data (per WHATWG spec, the WebSocket
        // error event is intentionally generic for security). Surface
        // the error only when we actually have a useful message.
        const message = event && event.message;
        if (message) {
          console.warn('Video call WebSocket error:', message);
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, []);

  // Connect WebSocket on mount
  useEffect(() => {
    if (user) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, connectWebSocket]);

  // ============== Polling Fallback for Incoming Calls ==============
  const pollingRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  
  // Track WebSocket connection status
  useEffect(() => {
    const checkWsStatus = () => {
      setWsConnected(wsRef.current?.readyState === WebSocket.OPEN);
    };
    
    const interval = setInterval(checkWsStatus, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Poll for incoming calls when WebSocket is not connected
  useEffect(() => {
    // Always poll for incoming calls as a fallback since WebSockets may not work in this environment
    const pollIncomingCalls = async () => {
      if (!user || incomingCall || callState.status !== CallStatus.IDLE) return;
      
      try {
        const res = await api.get('/video-calls/incoming');
        const calls = res.data.incoming_calls || [];
        
        if (calls.length > 0) {
          // Show the first incoming call
          const call = calls[0];
          setIncomingCall({
            callId: call.call_id,
            caller: call.caller,
            callMode: call.call_mode,
            providerType: call.provider_type
          });
          
          // Show native push notification for incoming call (iOS PWA support)
          notificationService.showIncomingCall(
            call.caller,
            call.call_id,
            call.call_mode === 'video'
          ).catch(err => console.log('[Notification] Could not show:', err));
        }
      } catch (error) {
        // Silent fail - just polling
      }
    };
    
    // Start polling
    pollIncomingCalls(); // Check immediately
    pollingRef.current = setInterval(pollIncomingCalls, 2000); // Check every 2 seconds
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [user, api, incomingCall, callState.status]);

  // ============== WebSocket Message Handlers ==============
  
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'incoming_call':
        // Show incoming call UI
        setIncomingCall({
          callId: data.call_id,
          caller: data.caller,
          callMode: data.call_mode,
          providerType: data.provider_type
        });
        
        // Show native push notification for incoming call (iOS PWA support)
        notificationService.showIncomingCall(
          data.caller,
          data.call_id,
          data.call_mode === 'video'
        ).catch(err => console.log('[Notification] Could not show:', err));
        break;
        
      case 'call_accepted':
        // Don't bump primary call status when the SECONDARY leg is accepted.
        if (secondaryCallIdRef.current && data.call_id === secondaryCallIdRef.current) {
          // Secondary accepted — toast for A's situational awareness.
          toast.success('Teammate connected — you can brief them now.');
          break;
        }
        setCallState(prev => ({
          ...prev,
          status: CallStatus.CONNECTING
        }));
        break;
        
      case 'call_declined':
        if (secondaryCallIdRef.current && data.call_id === secondaryCallIdRef.current) {
          // Secondary teammate declined — clean up briefly and unhold B.
          (async () => {
            try { await teardownSecondaryRef.current?.({ endBackend: true }); } catch (e) { /* ignore */ }
            try {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  type: 'unhold',
                  call_id: callStateRef.current?._pendingWarmTransfer?.heldCallId,
                }));
              }
            } catch (e) { /* ignore */ }
            setCallState((prev) => ({ ...prev, _pendingWarmTransfer: null, isOnHold: false }));
            toast.info('Teammate declined — back on the original call.');
          })();
          break;
        }
        setCallState(prev => ({
          ...prev,
          status: CallStatus.DECLINED
        }));
        setTimeout(() => resetCallState(), 2000);
        break;
        
      case 'call_ended':
        // If the SECONDARY (A↔C in conference) ended, just tear down that
        // leg — keep the primary call alive. Otherwise it's the primary
        // ending → run handleCallEnded normally.
        if (secondaryCallIdRef.current && data.call_id === secondaryCallIdRef.current) {
          (async () => {
            try { await teardownSecondaryRef.current?.({ endBackend: false }); } catch (e) { /* ignore */ }
            // Unhold B + clear pending so A is back on a clean A↔B call.
            setCallState((prev) => {
              const heldId = prev?._pendingWarmTransfer?.heldCallId;
              if (heldId && wsRef.current?.readyState === WebSocket.OPEN) {
                try { wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: heldId })); } catch (e) { /* ignore */ }
              }
              return { ...prev, _pendingWarmTransfer: null, _isInConference: false, isOnHold: false };
            });
            try {
              const stream = mediaState?.localStream;
              if (stream) stream.getAudioTracks().forEach((t) => { t.enabled = true; });
            } catch (e) { /* ignore */ }
            toast.info('Teammate left — you\'re back on the original call.');
          })();
        } else {
          handleCallEnded();
        }
        break;
        
      case 'call_cancelled':
        setIncomingCall(null);
        break;
        
      case 'offer':
        handleReceivedOffer(data.call_id, data.sdp);
        break;
        
      case 'answer':
        // Route the SDP answer to the correct peer connection based on
        // call_id — primary (A↔B) vs secondary (A↔C in 3-way conference).
        if (secondaryCallIdRef.current && data.call_id === secondaryCallIdRef.current && secondaryPeerConnectionRef.current) {
          try {
            secondaryPeerConnectionRef.current.setRemoteDescription(
              new RTCSessionDescription({ type: 'answer', sdp: data.sdp })
            );
          } catch (e) { console.error('[SECONDARY] setRemoteDescription failed:', e); }
        } else {
          handleReceivedAnswer(data.sdp);
        }
        break;
        
      case 'ice_candidate':
        // Same call_id-based dispatch for incoming ICE candidates.
        if (secondaryCallIdRef.current && data.call_id === secondaryCallIdRef.current && secondaryPeerConnectionRef.current) {
          try {
            secondaryPeerConnectionRef.current.addIceCandidate(
              new RTCIceCandidate({
                candidate: data.candidate,
                sdpMid: data.sdp_mid,
                sdpMLineIndex: data.sdp_m_line_index,
              })
            );
          } catch (e) { console.error('[SECONDARY] addIceCandidate failed:', e); }
        } else {
          handleReceivedIceCandidate(data);
        }
        break;

      case 'hold':
        // The remote peer pressed Hold — we are now the held party.
        // Play the hold music locally on a loop until we receive `unhold`.
        //
        // iter146 — Production-grade fix for the "music plays for BOTH
        // users" acoustic loopback bug. Before this change, B (the
        // held party) played hold music through its local speakers,
        // and B's still-LIVE microphone picked it up and streamed it
        // straight back to A over WebRTC — making A hear the very
        // music it had just started for B. Symmetric to A's
        // toggleHold, we now replace OUR (B's) audio sender on the
        // primary peer with a silent track for the duration of the
        // hold. Hold music continues to play LOCALLY on B's device;
        // A hears nothing. The original mic track is restored on
        // 'unhold' below.
        try {
          const silent = ensureSilentTrack();
          // Stash B's real mic track on the connection itself so the
          // unhold handler can find it again without depending on
          // closures (mediaState may have re-rendered by then).
          const pc = peerConnectionRef.current;
          if (pc && !pc._heldOriginalMic) {
            const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
            pc._heldOriginalMic = sender?.track || null;
          }
          replaceAudioSenderTrack(pc, silent);
        } catch (e) { /* ignore */ }
        setCallState((prev) => ({ ...prev, _heldByOther: true }));
        if (playHoldMusicRef.current) playHoldMusicRef.current();
        break;

      case 'unhold':
        // Restore OUR mic on the primary peer (iter146 — counterpart to the
        // silent-track replacement above).
        try {
          const pc = peerConnectionRef.current;
          const original = pc?._heldOriginalMic;
          if (original) {
            replaceAudioSenderTrack(pc, original);
            pc._heldOriginalMic = null;
          }
        } catch (e) { /* ignore */ }
        setCallState((prev) => ({ ...prev, _heldByOther: false }));
        if (stopHoldMusicRef.current) stopHoldMusicRef.current();
        break;

      case 'video_state':
        // The remote peer turned their camera ON or OFF mid-call.
        // We don't have to do much locally — the track will appear or disappear
        // on the existing remote MediaStream automatically because our peer
        // connection's `ontrack` handler updates state. We just bump UI flags.
        // IMPORTANT: if we were in voice-only mode and the remote escalates
        // to video, switch our renderer from <audio> to <video> so the new
        // track is actually shown. (Without this the remote video lands on a
        // hidden <audio> element and the user sees nothing.)
        setCallState((prev) => {
          const next = { ...prev, _remoteCameraOn: !!data.enabled };
          if (data.enabled && prev.callMode === 'voice') {
            next.callMode = 'video';
          }
          return next;
        });
        break;

      case 'renegotiate-offer':
        if (handleRenegotiateOfferRef.current) handleRenegotiateOfferRef.current(data);
        break;

      case 'renegotiate-answer':
        if (handleRenegotiateAnswerRef.current) handleRenegotiateAnswerRef.current(data);
        break;

      case 'transfer-redirect':
        if (handleTransferRedirectRef.current) handleTransferRedirectRef.current(data);
        break;

      case 'transfer-arming':
        // Purely informational — A is preparing a warm transfer; no UI change required.
        break;
        
      default:
        console.log('Unknown WebSocket message type:', data.type);
    }
  }, []);

  // ============== Call Lifecycle ==============
  
  const initiateCall = useCallback(async (calleeId, callMode = 'video') => {
    try {
      setCallState(prev => ({
        ...prev,
        status: CallStatus.INITIATING
      }));
      
      // Request media permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: callMode === 'video',
        audio: true
      });
      
      setMediaState(prev => ({
        ...prev,
        localStream: stream,
        isCameraOn: callMode === 'video',
        isMicOn: true
      }));
      
      // Call backend to initiate
      const response = await api.post('/video-calls/initiate', {
        callee_id: calleeId,
        call_mode: callMode
      });
      
      const { call_id, provider_type, caller, callee } = response.data;
      
      setCallState(prev => ({
        ...prev,
        status: CallStatus.CALLING,
        callId: call_id,
        providerType: provider_type,
        participants: [caller, callee],
        isCaller: true,
        callMode,
      }));
      
      // Navigate to Video Calls tab
      navigateToVideoCallsTab();

      // Caller-side ringback tone
      try { startRingback(); } catch (e) { /* ignore */ }

      // Internal-call ring duration matches the external dial timeout
      // (Twilio `<Dial timeout=30>` in the IVR — 30s total ringing
      // window for the called party before voicemail). Keeping the
      // two aligned means a user always has the same window to answer
      // regardless of whether the call came from a state pool number
      // or an internal teammate.
      try {
        if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
        let timeoutSec = 30;
        try {
          const s = await api.get('/calling/settings');
          timeoutSec = Number(s.data?.ring_timeout_seconds) || 30;
        } catch (e) { /* ignore */ }
        const callIdForTimeout = call_id;
        ringTimeoutRef.current = setTimeout(() => {
          setCallState((prev) => {
            // Guard 1: still on this call. Race-condition safe.
            if (prev.callId !== callIdForTimeout) return prev;
            // Guard 2: not already connected/ended/voicemail. We
            // intentionally treat ANY non-connected ringing-side
            // state as "still ringing" so a stuck UI gets bounced
            // to the voicemail prompt (was: only CALLING/INITIATING,
            // which left edge-cases stuck on the dial-tone forever).
            const stuck = ![
              CallStatus.CONNECTED,
              CallStatus.CONNECTING,
              CallStatus.VOICEMAIL,
              CallStatus.ENDED,
              CallStatus.DECLINED,
              CallStatus.MISSED,
              CallStatus.FAILED,
              CallStatus.IDLE,
            ].includes(prev.status);
            if (stuck) {
              setTimeout(() => {
                startVoicemailFlow().catch((err) => {
                  console.warn('[ring-timeout] voicemail flow failed, forcing end:', err);
                  try { endCall(); } catch (e) { /* ignore */ }
                });
              }, 0);
            }
            return prev;
          });
        }, timeoutSec * 1000);
      } catch (e) { /* ignore */ }
      
      // Subscribe to call events
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe',
          call_id: call_id
        }));
      }
      
      // If WebRTC call, set up peer connection
      if (provider_type === CallProviderType.NATIVE_WEBRTC) {
        await setupPeerConnection(call_id, stream, true);
      }

      // Start client-side recording of THIS leg's microphone (uploaded after call)
      try {
        startCallRecording(stream);
      } catch (e) { /* non-fatal */ }

      // Pre-unlock hold-music audio while we still have a recent user gesture
      // (this initiate click). Without this, B's browser will silently refuse
      // to autoplay when the 'hold' WS message arrives later.
      try { primeHoldMusic(); } catch (e) { /* non-fatal */ }
      
      return response.data;
    } catch (error) {
      console.error('Failed to initiate call:', error);
      resetCallState();
      throw error;
    }
  }, [api]);

  const answerCall = useCallback(async () => {
    if (!incomingCall) return;
    
    try {
      const callerInfo = incomingCall.caller || {};
      const callerParticipant = {
        user_id: callerInfo.user_id,
        name: callerInfo.name || 'Unknown',
        role: callerInfo.role,
        profile_picture: callerInfo.profile_picture,
        is_caller: true,
      };
      const calleeParticipant = {
        user_id: user?.id,
        name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'You',
        role: user?.role,
        profile_picture: user?.profile_picture,
        is_caller: false,
      };
      setCallState(prev => ({
        ...prev,
        status: CallStatus.CONNECTING,
        callId: incomingCall.callId,
        providerType: incomingCall.providerType,
        isCaller: false,
        callMode: incomingCall.callMode,
        participants: [callerParticipant, calleeParticipant],
      }));
      
      // Get media stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: incomingCall.callMode === 'video',
        audio: true
      });
      
      setMediaState(prev => ({
        ...prev,
        localStream: stream,
        isCameraOn: incomingCall.callMode === 'video',
        isMicOn: true
      }));
      
      // Navigate to Video Calls tab
      navigateToVideoCallsTab();
      
      // Subscribe to call events
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'subscribe',
          call_id: incomingCall.callId
        }));
      }
      
      // Call backend to answer first (this also retrieves the offer SDP)
      const answerRes = await api.post(`/video-calls/${incomingCall.callId}/answer`);
      
      // For WebRTC, we need to set up peer connection and handle the offer
      if (incomingCall.providerType === CallProviderType.NATIVE_WEBRTC) {
        // Set up peer connection as callee (not creating offer)
        await setupPeerConnectionAsCallee(incomingCall.callId, stream, answerRes.data.offer_sdp);
      }
      // Start client-side recording of this leg's microphone
      try { startCallRecording(stream); } catch (e) { /* non-fatal */ }

      // Pre-unlock hold-music audio while we still have a fresh user gesture
      // (this answer click). The 'hold' WS message will fire .play() later
      // outside any user gesture, which Safari blocks unless we've unlocked.
      try { primeHoldMusic(); } catch (e) { /* non-fatal */ }

      setIncomingCall(null);
    } catch (error) {
      console.error('Failed to answer call:', error);
      resetCallState();
      setIncomingCall(null);
      throw error;
    }
  }, [api, incomingCall, navigateToVideoCallsTab, user]);

  const declineCall = useCallback(async (reason = null) => {
    if (!incomingCall) return;
    
    try {
      await api.post(`/video-calls/${incomingCall.callId}/decline`, { reason });
      setIncomingCall(null);
    } catch (error) {
      console.error('Failed to decline call:', error);
      setIncomingCall(null);
    }
  }, [api, incomingCall]);

  const endCall = useCallback(async () => {
    if (!callState.callId) return;

    // If a secondary call (3-way conference or pending Add Participant) is
    // active, end it first so C's UI is dismissed cleanly.
    if (secondaryCallIdRef.current) {
      try { await teardownSecondaryRef.current?.({ endBackend: true }); } catch (e) { /* ignore */ }
    }

    // Defensive: if B was held (warm/add was in flight), send unhold + restore
    // their incoming audio before we end the primary, so B's hold music never
    // outlives the call even if the call_ended broadcast races.
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: callState.callId }));
      }
    } catch (e) { /* ignore */ }

    try {
      await api.post(`/video-calls/${callState.callId}/end`);
    } catch (error) {
      console.error('Failed to end call:', error);
    }
    
    handleCallEnded();
  }, [api, callState.callId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelCall = useCallback(async () => {
    if (!callState.callId || !callState.isCaller) return;

    // Stop the ringback tone immediately so the dial tone doesn't
    // outlive the UI. Also clear the ring-timeout so we don't fall
    // into the voicemail flow after cancelling.
    try { stopRingback(); } catch (e) { /* ignore */ }
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }

    try {
      await api.post(`/video-calls/${callState.callId}/cancel`);
    } catch (error) {
      console.error('Failed to cancel call:', error);
    }

    resetCallState();
  }, [api, callState.callId, callState.isCaller]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============== Hold music + Recording + Hold/Transfer ==============

  const startCallRecording = useCallback((stream) => {
    if (!stream || !window.MediaRecorder) return;
    try {
      // Audio-only track for transcription
      const audioOnly = new MediaStream(stream.getAudioTracks());
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const rec = mime ? new MediaRecorder(audioOnly, { mimeType: mime }) : new MediaRecorder(audioOnly);
      recordedChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      rec.start(2000); // collect chunks every 2s for resilience
      mediaRecorderRef.current = rec;
    } catch (e) {
      console.warn('[REC] could not start recorder:', e?.message || e);
    }
  }, []);

  const stopAndUploadRecording = useCallback(async (callId) => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive' || !callId) return;
    return new Promise((resolve) => {
      rec.onstop = async () => {
        try {
          const blob = new Blob(recordedChunksRef.current, { type: rec.mimeType || 'audio/webm' });
          recordedChunksRef.current = [];
          mediaRecorderRef.current = null;
          if (!blob || blob.size < 1000) { resolve(); return; }
          const ext = (rec.mimeType || '').includes('mp4') ? 'm4a' : 'webm';
          const fd = new FormData();
          fd.append('file', new File([blob], `leg.${ext}`, { type: blob.type }));
          await api.post(`/video-calls/${callId}/recording`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (e) {
          console.warn('[REC] upload failed:', e?.message || e);
        } finally {
          resolve();
        }
      };
      rec.stop();
    });
  }, [api]);

  // Pre-unlock an Audio element while we still have a fresh user gesture
  // (the click that answered/initiated the call). Browsers — Safari especially
  // — will then allow `.play()` to succeed later when the WebSocket relays a
  // 'hold' event to us, even though that arrival is NOT a user-gesture.
  const primeHoldMusic = useCallback(async () => {
    try {
      if (primedHoldAudioRef.current) return;
      const settings = (await api.get('/calling/settings')).data;
      const url = settings?.hold_music_url
        ? `${process.env.REACT_APP_BACKEND_URL || ''}${settings.hold_music_url}`
        : null;
      if (!url) return;
      const a = new Audio(url);
      a.loop = true;
      a.preload = 'auto';
      // Start MUTED + volume 0 to unlock the element silently. We keep
      // both flags set even after pause() because race-conditions in
      // Safari/Chrome can cause `await play()` to resolve AFTER our
      // `pause()` call → a brief audible burst of hold music at the
      // very start of the call. By keeping the element fully silent
      // until `playHoldMusicLoop` actually wants it, we eliminate
      // that startup noise entirely.
      a.muted = true;
      a.volume = 0;
      try { await a.play(); } catch (e) { /* ignore */ }
      try { a.pause(); a.currentTime = 0; } catch (e) { /* ignore */ }
      // DO NOT unmute or restore volume here — `playHoldMusicLoop`
      // will do that when an actual `hold` event arrives.
      primedHoldAudioRef.current = a;
    } catch (e) { /* ignore */ }
  }, [api]);

  const playHoldMusicLoop = useCallback(async () => {
    // Always tear down anything that's playing first so we never accumulate
    // multiple audio elements (which would otherwise silently overlap and
    // make 'unhold' appear to fail).
    const tearOld = (a) => {
      if (!a) return;
      try { a.pause(); a.src = ''; a.load(); } catch (e) { /* ignore */ }
    };
    tearOld(holdAudioRef.current);
    holdAudioRef.current = null;

    try {
      const settings = (await api.get('/calling/settings')).data;
      if (!settings?.hold_music_url) return;
      const url = `${process.env.REACT_APP_BACKEND_URL || ''}${settings.hold_music_url}`;
      const a = new Audio(url);
      a.loop = true;
      a.volume = 1.0;
      holdAudioRef.current = a;
      try { await a.play(); }
      catch (e) {
        console.warn('[HOLD] audio play blocked:', e?.message || e);
        // If a primed element is still alive (recently warmed up by a
        // user-gesture), use it as a last-resort fallback.
        const primed = primedHoldAudioRef.current;
        if (primed) {
          try {
            primed.currentTime = 0;
            primed.volume = 1.0;
            primed.muted = false;
            await primed.play();
            holdAudioRef.current = primed;
          } catch (e2) { /* ignore */ }
        }
      }
    } catch (e) { /* ignore */ }
  }, [api]);

  const stopHoldMusic = useCallback(() => {
    // Aggressive cleanup — pause AND tear down the audio element so playback
    // CANNOT outlive an unhold / call_ended event, even if pause() races with
    // an in-flight buffering operation.
    const teardown = (a) => {
      if (!a) return;
      try { a.pause(); } catch (e) { /* ignore */ }
      try { a.muted = true; } catch (e) { /* ignore */ }
      try { a.volume = 0; } catch (e) { /* ignore */ }
      try { a.currentTime = 0; } catch (e) { /* ignore */ }
      try { a.src = ''; } catch (e) { /* ignore */ }
      try { a.removeAttribute('src'); } catch (e) { /* ignore */ }
      try { a.load(); } catch (e) { /* ignore */ }
    };
    teardown(holdAudioRef.current);
    teardown(primedHoldAudioRef.current);
    holdAudioRef.current = null;
    primedHoldAudioRef.current = null;
  }, []);

  // Play the admin-uploaded "call connect" disclaimer once when a call
  // Play the admin-uploaded "call recording" disclaimer once the call
  // connects. Honors the per-call-type toggles in /calling/settings:
  //   call_start_audio_team_calls  → voice (team) calls
  //   call_start_audio_video_calls → video calls
  // Idempotent — won't double-play if the connectionState fires twice.
  //
  // For INTERNAL calls (user-to-user) we ALWAYS play the disclaimer
  // regardless of role (including admin participants) — per product
  // requirement that every internal call carry the recording
  // disclosure. The audio file used is the admin-uploaded clip if
  // present, otherwise the bundled system default. Autoplay is
  // pre-warmed via a global user-gesture listener so the play() is
  // not blocked by browser autoplay policy.
  const callStartPlayedRef = useRef(null);
  const disclaimerAudioRef = useRef(null);
  const disclaimerWarmedRef = useRef(false);

  // Pre-warm the disclaimer audio element on the first user gesture
  // so it can autoplay later without a "DOMException: play() failed"
  // when the WebRTC peer connection transitions to 'connected'.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Skip on public/unauth routes — visitors of the public business
    // card were briefly hearing the disclaimer audio when the warm()
    // play() ran on first tap.
    if (!isAuthenticated || !user) return undefined;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    // Use the system default; will be swapped to the admin-uploaded URL
    // (if any) lazily at play time.
    const audio = new Audio(`${apiBase}/api/media/calling/system_call_disclaimer.mp3`);
    audio.preload = 'auto';
    audio.volume = 1.0;
    disclaimerAudioRef.current = audio;
    // ROOT-CAUSE FIX (mirror of the one in TwilioVoiceContext): don't
    // touch the disclaimer audio element at all during the first-click
    // unlock — just wake the AudioContext with a silent 1-sample
    // buffer. The prior muted-play-pause dance still produced audible
    // clicks on iOS, which the user could hear as the disclaimer
    // playing on app open.
    const warm = () => {
      if (disclaimerWarmedRef.current) return;
      disclaimerWarmedRef.current = true;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const buf = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('click', warm, { capture: true });
    window.addEventListener('keydown', warm, { capture: true });
    window.addEventListener('touchstart', warm, { capture: true });
    return () => {
      window.removeEventListener('click', warm, { capture: true });
      window.removeEventListener('keydown', warm, { capture: true });
      window.removeEventListener('touchstart', warm, { capture: true });
      try { audio.pause(); } catch (e) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  const playCallStartDisclaimer = useCallback(async (callId, callMode) => {
    if (!callId) return;
    if (callStartPlayedRef.current === callId) return; // already played for this call
    callStartPlayedRef.current = callId;
    try {
      const res = await api.get('/calling/settings');
      const s = res.data || {};
      // INTERNAL calls (user-to-user) ALWAYS play the disclaimer
      // regardless of toggle. The admin toggles
      // call_start_audio_video_calls / call_start_audio_team_calls
      // historically gated this, but per product requirement every
      // internal call must carry the recording disclosure for both
      // parties. We only respect the toggle for EXTERNAL/PSTN flows.
      const audioUrl = s.call_start_audio_url || '/api/media/calling/system_call_disclaimer.mp3';
      const url = `${process.env.REACT_APP_BACKEND_URL || ''}${audioUrl}`;
      // Build a FRESH element every time so we never inherit a stale
      // paused/buffering state from the warm-up element. The warm-up
      // already unlocked the AudioContext via a 1-sample silent buffer
      // on first user gesture, which lets <audio> autoplay succeed.
      const fresh = new Audio(url);
      fresh.volume = 1.0;
      fresh.preload = 'auto';
      try {
        await fresh.play();
      } catch (e) {
        console.warn('[CALL-START] disclaimer fresh autoplay blocked, retrying once:', e?.message || e);
        // One retry after a short tick — sometimes the element needs
        // the buffering pipeline to spin up before it accepts play().
        await new Promise((r) => setTimeout(r, 150));
        try { await fresh.play(); } catch (e2) { /* give up — keep call alive */ }
      }
      void callMode;
    } catch (e) { /* ignore */ }
  }, [api]);

  // Wire the forward refs so handleWebSocketMessage can call them on incoming
  // hold/unhold from the remote peer.
  useEffect(() => {
    playHoldMusicRef.current = playHoldMusicLoop;
    stopHoldMusicRef.current = stopHoldMusic;
  }, [playHoldMusicLoop, stopHoldMusic]);

  // toggleHold rules:
  //  • The user pressing Hold (A) is the one who initiates. They:
  //      - mute their outgoing audio + video (B can't hear/see them)
  //      - mute their *incoming* audio so they don't hear B either
  //      - send a `hold` signal to B over the call WS
  //  • B receives the signal:
  //      - starts hold-music playback locally on a loop
  //      - shows "On hold" UI
  //  • Symmetrical for unhold.
  // Hold music NEVER plays on A's device.
  // IMPORTANT: on unhold, restore audio track ONLY if user wasn't muted before.
  const toggleHold = useCallback(async () => {
    const next = !callState.isOnHold;
    const local = mediaState.localStream;
    const originalMic = local?.getAudioTracks()?.[0] || null;

    if (next) {
      // HOLD: silence A→B by replacing the PRIMARY peer's audio sender with a
      // dedicated silent track. We never touch local.getAudioTracks()[0]
      // .enabled because that track is also the source for the secondary peer
      // (and for the audio mixer during conference) — disabling it would
      // silence A everywhere.
      try {
        const silent = ensureSilentTrack();
        await replaceAudioSenderTrack(peerConnectionRef.current, silent);
      } catch (e) { /* ignore */ }
      // Disable video to B too (if applicable). Video tracks aren't shared
      // with the secondary peer in our hub-and-spoke model.
      try {
        local?.getVideoTracks()?.forEach((t) => {
          // Tag the track with the primary sender so we can restore selectively.
          t.enabled = false;
        });
      } catch (e) { /* ignore */ }
      // Tell B (over WS) so they play hold music.
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
          wsRef.current.send(JSON.stringify({ type: 'hold', call_id: callState.callId }));
        }
      } catch (e) { /* ignore */ }
      stopHoldMusic(); // never play hold music on A's side
    } else {
      // UNHOLD: restore A→B's mic and signal B.
      try {
        if (originalMic) {
          await replaceAudioSenderTrack(peerConnectionRef.current, originalMic);
        }
      } catch (e) { /* ignore */ }
      try {
        local?.getVideoTracks()?.forEach((t) => { t.enabled = !!mediaState.isCameraOn; });
      } catch (e) { /* ignore */ }
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
          wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: callState.callId }));
        }
      } catch (e) { /* ignore */ }
    }

    setCallState((prev) => ({ ...prev, isOnHold: next }));
  }, [callState.isOnHold, callState.callId, mediaState.localStream, mediaState.isCameraOn, stopHoldMusic]);

  // Cold transfer: hand the call off — A tells B (the other party in the
  // current call) to hang up cleanly and place a fresh outbound call to C.
  // C rings, B reconnects to C. A drops out completely.
  // Warm transfer: A puts B on hold, A places a fresh outbound call to C
  // (C rings normally). The original A↔B call stays alive but is held —
  // B keeps hearing hold music. A talks to C as a separate active call.
  // On Complete, A sends transfer-redirect on the HELD call so B rings C,
  // and ends both A↔B and A↔C cleanly. On Cancel, A ends both calls.
  const transferCall = useCallback(async ({ targetUserId, mode = 'cold', uiMode = null }) => {
    if (!callState.callId || !targetUserId) return;
    const other = (callState.participants || []).find((p) => p.user_id !== user?.id);
    if (!other) {
      toast.error('No remote participant to transfer to');
      return;
    }

    if (mode === 'warm') {
      // 'add' uses the 3-way conference path (keep B alive, place a SECOND
      // peer to C, allow Merge into a real conference). 'warm' is the same
      // initial step but Complete fires transfer-redirect (C takes over from
      // A and A drops out — classic warm transfer with briefing).
      const labelMode = uiMode || 'warm';
      const heldCallId = callState.callId;
      const heldCallMode = callState.callMode || 'voice';

      try {
        // Step 1: HOLD B properly — silence A→B by replacing the primary
        // peer's audio sender with a silent track. The shared mic track
        // remains alive for the secondary peer. B will play hold music
        // because of the 'hold' WS message and will mute their incoming
        // audio because of `_heldByOther`.
        try {
          const silent = ensureSilentTrack();
          await replaceAudioSenderTrack(peerConnectionRef.current, silent);
        } catch (e) { /* ignore */ }
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'hold', call_id: heldCallId }));
        }
        setCallState((prev) => ({ ...prev, isOnHold: true }));

        // Step 2: place a SECONDARY peer connection to C — primary peer to B
        // remains intact so we can merge later. Use forward-ref to avoid TDZ
        // since placeSecondaryCall is defined further down the file.
        await placeSecondaryCallRef.current?.(targetUserId, heldCallMode);

        // Step 3: mark UI mode so the call screen shows Merge / Cancel.
        setCallState((prev) => ({
          ...prev,
          _pendingWarmTransfer: {
            targetUserId,
            heldCallId,
            heldCallMode,
            heldOtherUserId: other.user_id,
            uiMode: labelMode,
          },
        }));

        if (labelMode === 'add') {
          toast.success('Calling teammate — once connected, click "Merge" to bring everyone onto the line.');
        } else {
          toast.success('Briefing — when ready, click "Complete" to hand the contact off to them.');
        }
      } catch (e) {
        console.error('warm/add flow failed:', e);
        toast.error(e?.response?.data?.detail || 'Could not call teammate.');
        // Roll back the hold so B doesn't sit silently.
        try {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: heldCallId }));
          }
        } catch (e2) { /* ignore */ }
        try {
          const stream = mediaState.localStream;
          if (stream) stream.getAudioTracks().forEach((t) => { t.enabled = true; });
        } catch (e2) { /* ignore */ }
        setCallState((prev) => ({ ...prev, isOnHold: false }));
      }
      return;
    }

    // ---- cold transfer ----
    // Tell B to drop this call cleanly and place a fresh outbound to C.
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
        wsRef.current.send(JSON.stringify({
          type: 'transfer-redirect',
          call_id: callState.callId,
          target_user_id: targetUserId,
          call_mode: callState.callMode || 'voice',
        }));
      }
      toast.success('Transferring — your contact will ring the new teammate.');
      // End A's leg locally; B will end their own once the WS arrives.
      try { await api.post(`/video-calls/${callState.callId}/end`); } catch (e) { /* ignore */ }
      handleCallEnded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
    }
  }, [api, callState.callId, callState.participants, callState.callMode, mediaState.localStream, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const completeWarmTransfer = useCallback(async () => {
    const pending = callStateRef.current?._pendingWarmTransfer;
    if (!pending?.targetUserId || !pending.heldCallId) {
      toast.error('No transfer pending');
      return;
    }
    const isAdd = pending.uiMode === 'add';

    if (isAdd) {
      // ADD PARTICIPANT → MERGE: build the audio mixer and bring all 3
      // parties onto the same line. B and C remain in their own peer
      // connections to A; A's mixed-audio track flows to each.
      const ok = await mergeIntoConferenceRef.current?.();
      return ok;
    }

    // WARM TRANSFER → COMPLETE: hand B off to C, A drops out.
    try {
      // 1. Tell B (on the HELD call) to drop and ring C directly.
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'transfer-redirect',
          call_id: pending.heldCallId,
          target_user_id: pending.targetUserId,
          call_mode: pending.heldCallMode || 'voice',
        }));
      }

      // 2. Tear down the secondary call to C (A drops out of the brief leg).
      await teardownSecondaryRef.current?.({ endBackend: true });

      // 3. Clean up the held A↔B record on the backend after a small delay so
      //    B's transfer-redirect WS arrives before any call_ended.
      setTimeout(async () => {
        try { await api.post(`/video-calls/${pending.heldCallId}/end`); } catch (e) { /* ignore */ }
      }, 750);

      toast.success('Transfer complete — connecting them now.');
      handleCallEnded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete transfer');
    }
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelWarmTransfer = useCallback(async () => {
    const pending = callStateRef.current?._pendingWarmTransfer;

    // 1. Send 'unhold' to B FIRST so their UI clears _heldByOther, stops the
    //    hold music, and unmutes incoming audio — even if subsequent steps
    //    throw, B is no longer stuck in the held state.
    try {
      if (pending?.heldCallId && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: pending.heldCallId }));
      }
    } catch (e) { /* ignore */ }

    // 2. Restore A→B's audio sender to the original mic track. The primary
    //    peer connection was never closed, so this is a clean resume.
    try {
      const originalMic = mediaState.localStream?.getAudioTracks()?.[0];
      if (originalMic) {
        await replaceAudioSenderTrack(peerConnectionRef.current, originalMic);
      }
    } catch (e) { /* ignore */ }

    // 3. Tear down the secondary call to C — A drops the briefing leg.
    //    /end on the secondary call_id only reaches A and C (subscribers of
    //    that call_id), NOT B — so B's primary call is unaffected.
    try { await teardownSecondaryRef.current?.({ endBackend: true }); } catch (e) { /* ignore */ }

    setCallState((prev) => ({ ...prev, _pendingWarmTransfer: null, isOnHold: false }));
    toast.info('Cancelled — you\'re back on the call.');
  }, [mediaState.localStream]);

  const addParticipantToCall = useCallback(async ({ targetUserId }) => {
    // True N-way audio mixing requires an SFU / media-server. Until that's
    // wired up, "Add Participant" reuses the same backend dance as a Warm
    // Transfer (B held, A talks to C alone) but the UI label says "Add
    // Participant" / "Merge" — and Merge fires `transfer-redirect` so B and
    // C are placed on the same call after A drops out.
    return await transferCall({ targetUserId, mode: 'warm', uiMode: 'add' });
  }, [transferCall]);

  // ============== Polling Functions (must be defined before setup functions) ==============
  
  // Polling for ICE candidates
  const icePollingRef = useRef(null);
  const processedCandidatesRef = useRef(new Set());
  const pendingCandidatesRef = useRef([]);
  
  const startPollingForIceCandidates = useCallback((callId) => {
    if (icePollingRef.current) clearInterval(icePollingRef.current);
    processedCandidatesRef.current = new Set();
    pendingCandidatesRef.current = [];
    
    console.log('[ICE] Starting ICE candidate polling for call:', callId);
    
    icePollingRef.current = setInterval(async () => {
      const pc = peerConnectionRef.current;
      if (!pc) {
        console.log('[ICE] No peer connection, stopping polling');
        clearInterval(icePollingRef.current);
        return;
      }
      
      // CRITICAL: Must have remote description before adding ICE candidates
      if (!pc.remoteDescription) {
        console.log('[ICE] Waiting for remote description before processing candidates...');
        return;
      }
      
      try {
        const res = await api.get(`/video-calls/${callId}/signaling`);
        const { ice_candidates, status } = res.data;
        
        // Process new ICE candidates
        for (const candidate of ice_candidates || []) {
          const candidateKey = `${candidate.candidate}-${candidate.sdp_mid}`;
          if (!processedCandidatesRef.current.has(candidateKey)) {
            processedCandidatesRef.current.add(candidateKey);
            
            const candidateType = candidate.candidate.includes('typ relay') ? 'RELAY' :
                                 candidate.candidate.includes('typ srflx') ? 'SRFLX' :
                                 candidate.candidate.includes('typ host') ? 'HOST' : 'UNKNOWN';
            console.log(`[ICE] Adding peer candidate (${candidateType}):`, candidate.candidate.substring(0, 80));
            try {
              await pc.addIceCandidate(
                new RTCIceCandidate({
                  candidate: candidate.candidate,
                  sdpMid: candidate.sdp_mid,
                  sdpMLineIndex: candidate.sdp_m_line_index
                })
              );
              console.log(`[ICE] Peer candidate (${candidateType}) added successfully`);
            } catch (e) {
              console.error('[ICE] Failed to add ICE candidate:', e.message);
            }
          }
        }
        
        // Stop polling if call ended or connection is established
        if (['ended', 'declined', 'failed', 'closed'].includes(status)) {
          console.log('[ICE] Call ended, stopping ICE polling');
          clearInterval(icePollingRef.current);
        }
        
        // Also check connection state
        if (pc.connectionState === 'connected') {
          console.log('[ICE] Connection established, reducing poll frequency');
          // Keep polling at slower rate in case of ICE restart
        }
      } catch (error) {
        console.error('[ICE] Polling error:', error);
      }
    }, 1000); // Poll every second for faster ICE exchange
  }, [api]);

  // Polling for answer (caller side) - since WebSocket may not work
  const answerPollingRef = useRef(null);
  const hasStartedIcePollingRef = useRef(false);
  
  const startPollingForAnswer = useCallback((callId, pc) => {
    if (answerPollingRef.current) clearInterval(answerPollingRef.current);
    hasStartedIcePollingRef.current = false;
    
    console.log('[CALLER] Starting poll for answer...');
    
    // DO NOT start ICE polling here - must wait for setRemoteDescription first
    
    answerPollingRef.current = setInterval(async () => {
      try {
        // Check call status first
        const callRes = await api.get(`/video-calls/${callId}`);
        const { status } = callRes.data;
        
        if (status === 'declined') {
          console.log('[CALLER] Call was declined');
          clearInterval(answerPollingRef.current);
          try { stopRingback(); } catch (e) {}
          if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
          setCallState(prev => ({ ...prev, status: CallStatus.DECLINED }));
          setTimeout(() => resetCallState(), 2000);
          return;
        }
        
        // Check for answer SDP in signaling session
        const sigRes = await api.get(`/video-calls/${callId}/signaling`);
        const { answer_sdp } = sigRes.data;
        
        if (answer_sdp && pc && pc.signalingState === 'have-local-offer') {
          console.log('[CALLER] Received answer SDP, setting remote description...');
          clearInterval(answerPollingRef.current);
          
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'answer',
            sdp: answer_sdp
          }));
          console.log('[CALLER] Remote description set successfully');
          
          // NOW we can safely start polling for ICE candidates
          if (!hasStartedIcePollingRef.current) {
            hasStartedIcePollingRef.current = true;
            console.log('[CALLER] Starting ICE candidate polling after remote description set');
            startPollingForIceCandidates(callId);
          }
        }
      } catch (error) {
        console.error('[CALLER] Polling for answer error:', error);
      }
    }, 1000);
  }, [api, startPollingForIceCandidates]);

  // Polling for offer (callee side) - in case offer wasn't ready when answer was sent
  const offerPollingRef = useRef(null);
  
  const startPollingForOffer = useCallback((callId, localStream) => {
    if (offerPollingRef.current) clearInterval(offerPollingRef.current);
    
    offerPollingRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/video-calls/${callId}/signaling`);
        const { offer_sdp } = res.data;
        
        if (offer_sdp && peerConnectionRef.current && !peerConnectionRef.current.remoteDescription) {
          clearInterval(offerPollingRef.current);
          
          console.log('Received offer via polling, setting up connection');
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription({
            type: 'offer',
            sdp: offer_sdp
          }));
          
          const answer = await peerConnectionRef.current.createAnswer();
          await peerConnectionRef.current.setLocalDescription(answer);
          
          await api.post(`/video-calls/${callId}/answer-sdp`, {
            sdp: answer.sdp
          });
          
          // Start ICE polling after setting remote description
          startPollingForIceCandidates(callId);
        }
      } catch (error) {
        console.error('Polling for offer error:', error);
      }
    }, 1000);
  }, [api, startPollingForIceCandidates]);

  // ============== WebRTC Setup ==============
  
  const setupPeerConnection = useCallback(async (callId, localStream, isCaller) => {
    // Refresh ICE servers (Twilio NTS tokens expire) before each new call.
    const ice = await refreshIceServers();
    console.log(`[CALLER] Setting up peer connection with ICE servers:`, ice.length, 'servers');

    const config = {
      iceServers: ice,
      iceCandidatePoolSize: 10,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceTransportPolicy: 'all'  // Try all connection types
    };
    
    console.log('[CALLER] RTCPeerConnection config:', JSON.stringify(config, null, 2));
    
    peerConnectionRef.current = new RTCPeerConnection(config);
    const pc = peerConnectionRef.current;
    
    // Add local tracks
    localStream.getTracks().forEach(track => {
      console.log(`[CALLER] Adding local track: ${track.kind}`);
      pc.addTrack(track, localStream);
    });
    
    // Handle remote stream
    pc.ontrack = (event) => {
      console.log('[CALLER] Received remote track:', event.track.kind, event.streams);
      if (event.streams && event.streams[0]) {
        console.log('[CALLER] Setting remote stream with tracks:', event.streams[0].getTracks().map(t => t.kind));
        setMediaState(prev => ({
          ...prev,
          remoteStream: event.streams[0]
        }));
      }
    };
    
    // Handle ICE candidates - send to backend
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        const candidateType = event.candidate.candidate.includes('typ relay') ? 'RELAY' :
                             event.candidate.candidate.includes('typ srflx') ? 'SRFLX' :
                             event.candidate.candidate.includes('typ host') ? 'HOST' : 'UNKNOWN';
        console.log(`[CALLER] ICE candidate (${candidateType}):`, event.candidate.candidate.substring(0, 80));
        try {
          await api.post(`/video-calls/${callId}/ice-candidate`, {
            candidate: event.candidate.candidate,
            sdp_mid: event.candidate.sdpMid,
            sdp_m_line_index: event.candidate.sdpMLineIndex
          });
        } catch (error) {
          console.error('[CALLER] Failed to send ICE candidate:', error);
        }
      } else {
        console.log('[CALLER] ICE gathering complete');
      }
    };
    
    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log('[CALLER] ICE connection state:', pc.iceConnectionState);
      console.log('[CALLER] ICE gathering state:', pc.iceGatheringState);
      console.log('[CALLER] Signaling state:', pc.signalingState);
      console.log('[CALLER] Connection state:', pc.connectionState);
      
      // If ICE fails, try to restart
      if (pc.iceConnectionState === 'failed') {
        console.log('[CALLER] ICE connection failed, attempting restart...');
        pc.restartIce();
      }
      
      // Check if we're connected
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('[CALLER] ICE connected/completed!');
      }
    };
    
    // Handle ICE gathering state
    pc.onicegatheringstatechange = () => {
      console.log('[CALLER] ICE gathering state changed:', pc.iceGatheringState);
    };
    
    // Handle negotiation needed
    pc.onnegotiationneeded = () => {
      console.log('[CALLER] Negotiation needed event fired');
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('[CALLER] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        // Stop ringback + cancel voicemail timeout — we connected.
        try { stopRingback(); } catch (e) {}
        if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
        setCallState(prev => ({
          ...prev,
          status: CallStatus.CONNECTED
        }));
        // Start duration timer
        startDurationTimer();
        // Mark as connected on backend
        api.post(`/video-calls/${callId}/connected`).catch(console.error);
        // Play admin call-start disclaimer (once) if toggled on for this call type.
        try { playCallStartDisclaimer(callId, callStateRef.current?.callMode); } catch (e) { /* ignore */ }
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        // Do NOT auto-call handleCallEnded here. Transient peer drops happen
        // routinely (network blips, mid-call renegotiation, the other end
        // tearing down its peer for warm-transfer). Calls should only end
        // via an explicit `/end` POST or a `transfer-redirect` signal — both
        // of which already drive `call_ended` over the WS. Auto-ending on
        // peer state caused B to drop out the moment A closed their peer
        // during Add Participant / Warm Transfer / Cold Transfer.
        console.log('[CALLER] Connection failed/disconnected — waiting for explicit end signal.');
      }
    };
    
    // If caller, create and send offer
    if (isCaller) {
      console.log('[CALLER] Creating offer...');
      const offer = await pc.createOffer();
      console.log('[CALLER] Setting local description...');
      await pc.setLocalDescription(offer);
      
      // Send offer to backend
      console.log('[CALLER] Sending offer to backend...');
      await api.post(`/video-calls/${callId}/offer`, {
        sdp: offer.sdp
      });
      
      // Start polling for answer - ICE polling will start AFTER remote description is set
      console.log('[CALLER] Starting to poll for answer...');
      startPollingForAnswer(callId, pc);
    }
  }, [api, refreshIceServers]);

  // Set up peer connection as callee (receives offer, sends answer)
  const setupPeerConnectionAsCallee = useCallback(async (callId, localStream, offerSdp) => {
    const ice = await refreshIceServers();
    console.log('[CALLEE] Setting up peer connection, have offer:', !!offerSdp);
    console.log('[CALLEE] Using ICE servers:', ice.length, 'servers');

    // Create peer connection with proper config
    const config = {
      iceServers: ice,
      iceCandidatePoolSize: 10,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceTransportPolicy: 'all'  // Try all connection types
    };
    
    console.log('[CALLEE] RTCPeerConnection config:', JSON.stringify(config, null, 2));
    
    peerConnectionRef.current = new RTCPeerConnection(config);
    const pc = peerConnectionRef.current;
    
    // Add local tracks
    localStream.getTracks().forEach(track => {
      console.log(`[CALLEE] Adding local track: ${track.kind}`);
      pc.addTrack(track, localStream);
    });
    
    // Handle remote stream
    pc.ontrack = (event) => {
      console.log('[CALLEE] Received remote track:', event.track.kind, event.streams);
      if (event.streams && event.streams[0]) {
        console.log('[CALLEE] Setting remote stream with tracks:', event.streams[0].getTracks().map(t => t.kind));
        setMediaState(prev => ({
          ...prev,
          remoteStream: event.streams[0]
        }));
      }
    };
    
    // Handle ICE candidates - send to backend
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        const candidateType = event.candidate.candidate.includes('typ relay') ? 'RELAY' :
                             event.candidate.candidate.includes('typ srflx') ? 'SRFLX' :
                             event.candidate.candidate.includes('typ host') ? 'HOST' : 'UNKNOWN';
        console.log(`[CALLEE] ICE candidate (${candidateType}):`, event.candidate.candidate.substring(0, 80));
        try {
          await api.post(`/video-calls/${callId}/ice-candidate`, {
            candidate: event.candidate.candidate,
            sdp_mid: event.candidate.sdpMid,
            sdp_m_line_index: event.candidate.sdpMLineIndex
          });
        } catch (error) {
          console.error('[CALLEE] Failed to send ICE candidate:', error);
        }
      } else {
        console.log('[CALLEE] ICE gathering complete');
      }
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('[CALLEE] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallState(prev => ({
          ...prev,
          status: CallStatus.CONNECTED
        }));
        startDurationTimer();
        api.post(`/video-calls/${callId}/connected`).catch(console.error);
        try { playCallStartDisclaimer(callId, callStateRef.current?.callMode); } catch (e) { /* ignore */ }
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        // See [CALLER] comment above — never auto-end on peer state changes.
        // Wait for an explicit end signal (call_ended over WS or transfer-redirect).
        console.log('[CALLEE] Connection failed/disconnected — waiting for explicit end signal.');
      }
    };
    
    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log('[CALLEE] ICE connection state:', pc.iceConnectionState);
      console.log('[CALLEE] ICE gathering state:', pc.iceGatheringState);
      console.log('[CALLEE] Signaling state:', pc.signalingState);
      console.log('[CALLEE] Connection state:', pc.connectionState);
      
      // If ICE fails, try to restart
      if (pc.iceConnectionState === 'failed') {
        console.log('[CALLEE] ICE connection failed, attempting restart...');
        pc.restartIce();
      }
      
      // Check if we're connected
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('[CALLEE] ICE connected/completed!');
      }
    };
    
    // Handle ICE gathering state
    pc.onicegatheringstatechange = () => {
      console.log('[CALLEE] ICE gathering state changed:', pc.iceGatheringState);
    };
    
    // Handle negotiation needed
    pc.onnegotiationneeded = () => {
      console.log('[CALLEE] Negotiation needed event fired');
    };
    
    // CRITICAL: Must set remote description BEFORE adding any ICE candidates
    if (offerSdp) {
      console.log('[CALLEE] Setting remote description (offer)...');
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: 'offer',
        sdp: offerSdp
      }));
      console.log('[CALLEE] Remote description set successfully');
      
      console.log('[CALLEE] Creating answer...');
      const answer = await pc.createAnswer();
      console.log('[CALLEE] Setting local description (answer)...');
      await pc.setLocalDescription(answer);
      
      // Send answer to backend
      console.log('[CALLEE] Sending SDP answer to backend');
      await api.post(`/video-calls/${callId}/answer-sdp`, {
        sdp: answer.sdp
      });
      console.log('[CALLEE] SDP answer sent, starting ICE candidate polling');
      
      // NOW we can safely poll for ICE candidates (remote description is set)
      startPollingForIceCandidates(callId);
    } else {
      console.warn('[CALLEE] No offer SDP received - will poll for it');
      startPollingForOffer(callId, localStream);
    }
  }, [api, refreshIceServers]);

  const handleReceivedOffer = useCallback(async (callId, sdp) => {
    if (!peerConnectionRef.current) return;
    
    const pc = peerConnectionRef.current;
    await pc.setRemoteDescription(new RTCSessionDescription({
      type: 'offer',
      sdp: sdp
    }));
    
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    
    // Send answer to backend
    await api.post(`/video-calls/${callId}/answer`, {
      sdp: answer.sdp
    });
  }, [api]);

  const handleReceivedAnswer = useCallback(async (sdp) => {
    if (!peerConnectionRef.current) return;
    
    await peerConnectionRef.current.setRemoteDescription(
      new RTCSessionDescription({
        type: 'answer',
        sdp: sdp
      })
    );
  }, []);

  const handleReceivedIceCandidate = useCallback(async (data) => {
    if (!peerConnectionRef.current) return;
    
    try {
      await peerConnectionRef.current.addIceCandidate(
        new RTCIceCandidate({
          candidate: data.candidate,
          sdpMid: data.sdp_mid,
          sdpMLineIndex: data.sdp_m_line_index
        })
      );
    } catch (error) {
      console.error('Failed to add ICE candidate:', error);
    }
  }, []);

  // ============== 3-way conference ==============
  // A acts as the audio-mixer hub: A↔B is the primary peer, A↔C is a SECOND
  // peer connection running in parallel. When A clicks "Merge", a Web Audio
  // graph mixes (A.mic + C.remote) → outgoing track on A↔B, and (A.mic +
  // B.remote) → outgoing track on A↔C, so all three parties hear each other.
  // No SFU required — but A's bandwidth/CPU bears the cost.

  // Set up the secondary peer connection (A↔C). Mirrors setupPeerConnection
  // but writes into secondaryPeerConnectionRef + secondaryRemoteStream.
  const setupSecondaryPeerConnection = useCallback(async (callId, localStream) => {
    const ice = await refreshIceServers();
    const config = {
      iceServers: ice,
      iceCandidatePoolSize: 10,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceTransportPolicy: 'all',
    };
    secondaryPeerConnectionRef.current = new RTCPeerConnection(config);
    const pc = secondaryPeerConnectionRef.current;

    // Add A's local audio (and video if present) to the secondary peer too.
    localStream.getTracks().forEach((t) => {
      try { pc.addTrack(t, localStream); } catch (e) { /* already added is fine */ }
    });

    pc.ontrack = (event) => {
      console.log('[SECONDARY] Received remote track:', event.track.kind);
      if (event.streams && event.streams[0]) {
        setMediaState((prev) => ({ ...prev, secondaryRemoteStream: event.streams[0] }));
      }
    };

    pc.onicecandidate = async (event) => {
      if (!event.candidate) return;
      try {
        await api.post(`/video-calls/${callId}/ice-candidate`, {
          candidate: event.candidate.candidate,
          sdp_mid: event.candidate.sdpMid,
          sdp_m_line_index: event.candidate.sdpMLineIndex,
        });
      } catch (e) { /* ignore — non-fatal */ }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed') {
        try { pc.restartIce(); } catch (e) { /* ignore */ }
      }
    };

    pc.onconnectionstatechange = () => {
      // No auto-end — same policy as the primary peer.
      console.log('[SECONDARY] Connection state:', pc.connectionState);
    };

    // Caller side: create offer, send to backend, then poll for the SDP answer
    // and remote ICE candidates tagged with this secondary call_id.
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await api.post(`/video-calls/${callId}/offer`, { sdp: offer.sdp });

    // Poll for SDP answer (the WS will deliver it too — see dispatch in
    // handleWebSocketMessage; this is a fallback for in-flight WS hiccups).
    let answerSet = false;
    secondaryAnswerPollingRef.current = setInterval(async () => {
      if (answerSet) return;
      try {
        const r = await api.get(`/video-calls/${callId}/answer`);
        if (r.data?.sdp && pc.signalingState !== 'stable') {
          answerSet = true;
          await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: r.data.sdp }));
          if (secondaryAnswerPollingRef.current) {
            clearInterval(secondaryAnswerPollingRef.current);
            secondaryAnswerPollingRef.current = null;
          }
        }
      } catch (e) { /* answer not ready yet */ }
    }, 1500);

    // Poll for remote ICE candidates.
    let lastIceIdx = 0;
    secondaryIcePollingRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/video-calls/${callId}/ice-candidates?from_index=${lastIceIdx}`);
        const cands = r.data?.candidates || [];
        for (const c of cands) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate({
              candidate: c.candidate,
              sdpMid: c.sdp_mid,
              sdpMLineIndex: c.sdp_m_line_index,
            }));
          } catch (e) { /* ignore */ }
        }
        lastIceIdx += cands.length;
      } catch (e) { /* ignore */ }
    }, 2000);
  }, [api, refreshIceServers]);

  // Place the second outbound call (A→C) without disturbing the primary peer.
  // The original A↔B call stays alive; B is held (call this AFTER sending the
  // 'hold' WS event).
  const placeSecondaryCall = useCallback(async (targetUserId, callMode = 'voice') => {
    const stream = mediaState.localStream;
    if (!stream) {
      throw new Error('No local stream — cannot place secondary call');
    }
    const response = await api.post('/video-calls/initiate', {
      callee_id: targetUserId,
      call_mode: callMode,
    });
    const { call_id } = response.data;
    secondaryCallIdRef.current = call_id;

    // Subscribe to the secondary call's WS events.
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', call_id }));
    }

    await setupSecondaryPeerConnection(call_id, stream);
    return response.data;
  }, [api, mediaState.localStream, setupSecondaryPeerConnection]);

  // Merge — wire up the Web Audio mixer and replace the outgoing audio tracks
  // on both peer connections so all three parties hear each other.
  const mergeIntoConference = useCallback(async () => {
    const pcB = peerConnectionRef.current;
    const pcC = secondaryPeerConnectionRef.current;
    const local = mediaState.localStream;
    const remoteB = mediaState.remoteStream;
    const remoteC = mediaState.secondaryRemoteStream;

    if (!pcB || !pcC || !local || !remoteB || !remoteC) {
      toast.error('Conference not ready — wait until both calls are connected.');
      return false;
    }

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();

      // Sources
      const micSrc = ctx.createMediaStreamSource(local);
      const remoteBSrc = ctx.createMediaStreamSource(new MediaStream(remoteB.getAudioTracks()));
      const remoteCSrc = ctx.createMediaStreamSource(new MediaStream(remoteC.getAudioTracks()));

      // Outgoing-to-B = mic + C
      const destForB = ctx.createMediaStreamDestination();
      micSrc.connect(destForB);
      remoteCSrc.connect(destForB);

      // Outgoing-to-C = mic + B
      const destForC = ctx.createMediaStreamDestination();
      micSrc.connect(destForC);
      remoteBSrc.connect(destForC);

      // Find the audio sender on each peer and replace its track with the
      // mixed track. Video senders are left untouched. (Uses the
      // module-scoped replaceAudioSenderTrack helper.)
      const mixedToBTrack = destForB.stream.getAudioTracks()[0];
      const mixedToCTrack = destForC.stream.getAudioTracks()[0];
      await replaceAudioSenderTrack(pcB, mixedToBTrack);
      await replaceAudioSenderTrack(pcC, mixedToCTrack);

      // Stash the mixer so we can tear it down on hangup / un-merge.
      audioMixerRef.current = {
        ctx,
        micSrc,
        remoteBSrc,
        remoteCSrc,
        destForB,
        destForC,
        originalMicTrack: local.getAudioTracks()[0] || null,
      };

      // Tell B their hold is over. We use the PRIMARY callId (which is also
      // the held call_id — same thing in our model) and broadcast TWICE with
      // a small delay just in case the first reaches before B's WS has
      // reconciled the previous 'hold'. Defensive but cheap.
      const heldCallId = callStateRef.current?._pendingWarmTransfer?.heldCallId
        || callStateRef.current?.callId;
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN && heldCallId) {
          wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: heldCallId }));
          setTimeout(() => {
            try {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'unhold', call_id: heldCallId }));
              }
            } catch (e) { /* ignore */ }
          }, 400);
        }
      } catch (e) { /* ignore */ }

      // Mark conference active and clear the warm-transfer bar.
      setCallState((prev) => ({
        ...prev,
        _isInConference: true,
        _pendingWarmTransfer: null,
        isOnHold: false,
      }));

      toast.success('All three parties connected — conference is live.');
      return true;
    } catch (e) {
      console.error('Merge failed:', e);
      toast.error('Could not merge into conference — check microphone access.');
      return false;
    }
  }, [mediaState.localStream, mediaState.remoteStream, mediaState.secondaryRemoteStream]); // eslint-disable-line

  // Tear down the secondary peer + audio mixer cleanly. Optionally calls
  // backend /end on the secondary call_id so the C-side gets call_ended.
  const teardownSecondary = useCallback(async ({ endBackend = true } = {}) => {
    try {
      if (secondaryAnswerPollingRef.current) { clearInterval(secondaryAnswerPollingRef.current); secondaryAnswerPollingRef.current = null; }
      if (secondaryIcePollingRef.current) { clearInterval(secondaryIcePollingRef.current); secondaryIcePollingRef.current = null; }
    } catch (e) { /* ignore */ }
    const sid = secondaryCallIdRef.current;
    if (endBackend && sid) {
      try { await api.post(`/video-calls/${sid}/end`); } catch (e) { /* ignore */ }
    }
    try { secondaryPeerConnectionRef.current?.close(); } catch (e) { /* ignore */ }
    secondaryPeerConnectionRef.current = null;
    secondaryCallIdRef.current = null;
    setMediaState((prev) => ({ ...prev, secondaryRemoteStream: null }));

    // If we were in a conference, also restore the primary peer's outgoing
    // audio track to the raw mic so audio continues to flow normally to B.
    const mixer = audioMixerRef.current;
    if (mixer) {
      try {
        const pcB = peerConnectionRef.current;
        if (pcB && mixer.originalMicTrack) {
          const senders = pcB.getSenders().filter((s) => s.track && s.track.kind === 'audio');
          for (const s of senders) {
            try { await s.replaceTrack(mixer.originalMicTrack); } catch (e) { /* ignore */ }
          }
        }
      } catch (e) { /* ignore */ }
      try { mixer.ctx.close(); } catch (e) { /* ignore */ }
      audioMixerRef.current = null;
    }
  }, [api]);

  // Reference to callState for use inside async callbacks (avoids stale closure).
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // ============== Media Controls ==============
  
  const toggleMic = useCallback(() => {
    if (mediaState.localStream) {
      const audioTrack = mediaState.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMediaState(prev => ({
          ...prev,
          isMicOn: audioTrack.enabled
        }));
      }
    }
  }, [mediaState.localStream]);

  // toggleCamera handles 4 cases:
  //   1. Camera on  → tap → disable existing video track (camera off, peer keeps the empty track)
  //   2. Camera off + track exists → tap → re-enable existing track
  //   3. No video track at all (voice call) → tap → request webcam, add the
  //      track to the peer connection, renegotiate via offer/answer, signal the
  //      remote so they expect a video stream.
  const toggleCamera = useCallback(async () => {
    const stream = mediaState.localStream;
    if (!stream) return;
    const existing = stream.getVideoTracks()[0];

    if (existing) {
      const next = !existing.enabled;
      existing.enabled = next;
      setMediaState((prev) => ({ ...prev, isCameraOn: next }));
      // Notify the remote peer so they can update their UI flag.
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
          wsRef.current.send(JSON.stringify({
            type: 'video_state',
            call_id: callState.callId,
            enabled: next,
          }));
        }
      } catch (e) { /* ignore */ }
      return;
    }

    // No video track — escalate voice-only call to video on this side only.
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      const newTrack = newStream.getVideoTracks()[0];
      if (!newTrack) return;
      stream.addTrack(newTrack);
      const pc = peerConnectionRef.current;
      if (pc) {
        // Add the new track. If we had no video track in the original offer,
        // a new transceiver/m-line is added — that requires renegotiation.
        pc.addTrack(newTrack, stream);
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          // Send the renegotiation offer over WebSocket so the peer receives it
          // immediately and processes it via their existing peer connection.
          if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
            wsRef.current.send(JSON.stringify({
              type: 'renegotiate-offer',
              call_id: callState.callId,
              sdp: offer.sdp,
            }));
          }
        } catch (e) {
          console.warn('[CAMERA] renegotiation failed:', e?.message || e);
        }
      }
      setMediaState((prev) => ({ ...prev, isCameraOn: true }));
      // Tell remote so they can expect/show the video stream
      try {
        if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
          wsRef.current.send(JSON.stringify({
            type: 'video_state',
            call_id: callState.callId,
            enabled: true,
          }));
        }
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error('Could not access camera');
    }
  }, [mediaState.localStream, callState.callId]);

  const toggleMinimize = useCallback(() => {
    setCallState(prev => ({
      ...prev,
      isMinimized: !prev.isMinimized
    }));
  }, []);

  // ============== Renegotiation handlers ==============
  // Apply an incoming SDP offer mid-call (e.g. when the remote escalated voice→video)
  const handleRenegotiateOffer = useCallback(async (data) => {
    const pc = peerConnectionRef.current;
    if (!pc || !data?.sdp) return;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: data.sdp }));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      if (wsRef.current?.readyState === WebSocket.OPEN && callState.callId) {
        wsRef.current.send(JSON.stringify({
          type: 'renegotiate-answer',
          call_id: callState.callId,
          sdp: answer.sdp,
        }));
      }
    } catch (e) {
      console.warn('[RENEG] handleRenegotiateOffer failed:', e?.message || e);
    }
  }, [callState.callId]);

  const handleRenegotiateAnswer = useCallback(async (data) => {
    const pc = peerConnectionRef.current;
    if (!pc || !data?.sdp) return;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: data.sdp }));
    } catch (e) {
      console.warn('[RENEG] handleRenegotiateAnswer failed:', e?.message || e);
    }
  }, []);

  // ============== Ringback tone (caller side) ==============
  // US-style ringback: 440 Hz + 480 Hz, 2s on / 4s off, until stopped.
  const startRingback = useCallback(() => {
    try {
      stopRingback();
    } catch (e) { /* ignore */ }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gain = ctx.createGain();
      gain.gain.value = 0; // silent until cycled on
      gain.connect(ctx.destination);
      const o1 = ctx.createOscillator();
      const o2 = ctx.createOscillator();
      o1.frequency.value = 440;
      o2.frequency.value = 480;
      o1.connect(gain);
      o2.connect(gain);
      o1.start();
      o2.start();
      const cycle = () => {
        try {
          const now = ctx.currentTime;
          gain.gain.cancelScheduledValues(now);
          gain.gain.setValueAtTime(0, now);
          gain.gain.linearRampToValueAtTime(0.18, now + 0.05);
          gain.gain.setValueAtTime(0.18, now + 1.95);
          gain.gain.linearRampToValueAtTime(0, now + 2.0);
        } catch (e) { /* ignore */ }
      };
      cycle();
      const interval = setInterval(cycle, 6000);
      ringbackCtxRef.current = ctx;
      ringbackNodesRef.current = { gain, o1, o2, interval };
    } catch (e) {
      console.warn('[RINGBACK] start failed:', e?.message || e);
    }
  }, []);

  const stopRingback = useCallback(() => {
    try {
      const nodes = ringbackNodesRef.current;
      if (nodes) {
        if (nodes.interval) clearInterval(nodes.interval);
        try { nodes.o1.stop(); } catch (e) {}
        try { nodes.o2.stop(); } catch (e) {}
      }
      ringbackNodesRef.current = null;
      const ctx = ringbackCtxRef.current;
      if (ctx) {
        try { ctx.close(); } catch (e) {}
      }
      ringbackCtxRef.current = null;
    } catch (e) { /* ignore */ }
  }, []);

  // ============== Voicemail flow (caller side) ==============
  // After ring timeout, end the outbound attempt and prompt the caller to leave a voicemail.
  const startVoicemailFlow = useCallback(async () => {
    try {
      stopRingback();
      const callId = callState.callId;
      const callee = (callState.participants || []).find((p) => p.user_id !== user?.id);
      if (!callee) {
        try { await endCall(); } catch (e) {}
        return;
      }
      // Fetch the recipient's greeting (system default or custom)
      let greetingUrl = null;
      try {
        const g = await api.get(`/voicemail/greeting-for/${callee.user_id}`);
        greetingUrl = g.data?.effective_greeting_url || null;
      } catch (e) { /* fall back to no greeting */ }

      setCallState((prev) => ({
        ...prev,
        status: CallStatus.VOICEMAIL,
        _voicemail: { recipientUserId: callee.user_id, recipientName: callee.name, callId, greetingUrl },
      }));
      // Stop ringback before greeting plays
      try {
        if (greetingUrl) {
          const url = `${process.env.REACT_APP_BACKEND_URL || ''}${greetingUrl}`;
          const a = new Audio(url);
          transferAudioRef.current = a;
          try { await a.play(); } catch (e) { /* autoplay may be blocked */ }
        }
      } catch (e) { /* ignore */ }
    } catch (e) {
      console.warn('[VOICEMAIL] startVoicemailFlow failed:', e?.message || e);
    }
  }, [callState.callId, callState.participants, user?.id, stopRingback, api]); // eslint-disable-line

  // Caller records a voicemail message; called from UI button.
  const recordAndSendVoicemail = useCallback(async () => {
    const vm = callState._voicemail;
    if (!vm?.recipientUserId) {
      toast.error('No voicemail target');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      const startedAt = Date.now();
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
      const stopped = new Promise((resolve) => { rec.onstop = resolve; });
      rec.start();
      // Auto-stop after 60s safeguard
      const safety = setTimeout(() => { try { rec.stop(); } catch (e) {} }, 60000);
      const stopFn = async () => {
        try { rec.stop(); } catch (e) {}
        clearTimeout(safety);
        await stopped;
        try { stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const fd = new FormData();
        fd.append('recipient_user_id', vm.recipientUserId);
        if (vm.callId) fd.append('call_id', vm.callId);
        fd.append('duration_sec', String(Math.round((Date.now() - startedAt) / 1000)));
        fd.append('file', blob, 'voicemail.webm');
        try {
          await api.post('/voicemail/leave', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          toast.success('Voicemail sent');
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Could not send voicemail');
        }
        voicemailStopRef.current = null;
        setCallState((prev) => ({ ...prev, _voicemail: null }));
        try { handleCallEnded(); } catch (e) {}
      };
      voicemailStopRef.current = stopFn;
    } catch (e) {
      toast.error('Could not access microphone');
    }
  }, [callState._voicemail, api]); // eslint-disable-line

  const stopVoicemailRecording = useCallback(() => {
    const fn = voicemailStopRef.current;
    if (fn) fn();
  }, []);

  const skipVoicemail = useCallback(() => {
    voicemailStopRef.current = null;
    setCallState((prev) => ({ ...prev, _voicemail: null }));
    try { handleCallEnded(); } catch (e) {}
  }, []); // eslint-disable-line

  // ============== Transfer redirect (B-side handler) ==============
  // Fired when A hands off the call: B drops the current call cleanly and
  // places a fresh outbound call to the target teammate so C rings.
  const handleTransferRedirect = useCallback(async (data) => {
    const target = data?.target_user_id;
    const mode = data?.call_mode || callState.callMode || 'voice';
    if (!target) return;

    // Look up the optional "please hold" snippet and the hold music URL up front.
    let pleaseHoldUrl = null;
    let holdMusicUrl = null;
    try {
      const s = await api.get('/calling/settings');
      pleaseHoldUrl = s.data?.please_hold_url || null;
      holdMusicUrl = s.data?.hold_music_url || null;
    } catch (e) { /* ignore */ }

    // Soft tear-down: close the peer connection + stop the local stream WITHOUT
    // running handleCallEnded (which would flip the UI to CallStatus.ENDED for
    // 2s). Instead we keep status at CONNECTING so B's UI shows a calm
    // "Connecting…" state while we briefly play please-hold / ringback and
    // then place the outbound call to C.
    try {
      [icePollingRef, answerPollingRef, offerPollingRef].forEach((ref) => {
        if (ref?.current) { try { clearInterval(ref.current); } catch (e) { /* ignore */ } ref.current = null; }
      });
    } catch (e) { /* ignore */ }
    try {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    } catch (e) { /* ignore */ }
    try { mediaState.localStream?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }

    // Reset just enough state — keep status visible as CONNECTING so the
    // call modal stays mounted and the user sees a continuous experience.
    setCallState((prev) => ({
      ...prev,
      status: CallStatus.CONNECTING,
      _heldByOther: false,
      _pendingWarmTransfer: null,
    }));

    // Audio bridge: please-hold (if configured) → short ringback → outbound to C.
    // Hold music keeps playing the whole time UNTIL please-hold starts.
    const playPleaseHold = () => new Promise((resolve) => {
      if (!pleaseHoldUrl) { resolve(); return; }
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL || ''}${pleaseHoldUrl}`;
        const a = new Audio(url);
        transferAudioRef.current = a;
        a.onended = () => resolve();
        a.onerror = () => resolve();
        // 8s safety timeout in case the file never finishes/loads.
        setTimeout(() => resolve(), 8000);
        a.play().catch(() => resolve());
      } catch (e) { resolve(); }
    });

    // Stop hold music ONLY when please-hold begins, so there's no silent gap.
    if (pleaseHoldUrl) {
      try { stopHoldMusic(); } catch (e) { /* ignore */ }
      await playPleaseHold();
    }

    // Brief ringback while we wait for C to answer — kept short because
    // initiateCall itself will start its own ringback once a peer offer is sent.
    try { startRingback(); } catch (e) { /* ignore */ }

    // Place new outbound call to C. initiateCall will overwrite callState
    // with a fresh CALLING / CONNECTING / CONNECTED progression.
    try {
      // Make sure no leftover hold music plays once we transition fully.
      stopHoldMusic();
      await initiateCall(target, mode);
    } catch (e) {
      try { stopRingback(); } catch (e2) { /* ignore */ }
      toast.error(e?.response?.data?.detail || 'Transfer redirect failed');
      try { handleCallEnded(); } catch (e2) { /* ignore */ }
    }
  }, [api, callState.callMode, mediaState.localStream, stopHoldMusic]); // eslint-disable-line

  // Wire up forward refs so the WS handler can call these without TDZ issues.
  useEffect(() => {
    handleRenegotiateOfferRef.current = handleRenegotiateOffer;
    handleRenegotiateAnswerRef.current = handleRenegotiateAnswer;
    handleTransferRedirectRef.current = handleTransferRedirect;
    teardownSecondaryRef.current = teardownSecondary;
    placeSecondaryCallRef.current = placeSecondaryCall;
    mergeIntoConferenceRef.current = mergeIntoConference;
  }, [handleRenegotiateOffer, handleRenegotiateAnswer, handleTransferRedirect, teardownSecondary, placeSecondaryCall, mergeIntoConference]);

  // ============== Helpers ==============
  
  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    durationIntervalRef.current = setInterval(() => {
      setCallState(prev => ({
        ...prev,
        duration: prev.duration + 1
      }));
    }, 1000);
  }, []);

  const handleCallEnded = useCallback(() => {
    console.log('[CALL] Handling call ended');
    
    // Stop duration timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Stop ringback tone + clear ring timeout
    try { stopRingback(); } catch (e) {}
    if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
    // Stop any transient transfer/voicemail audio
    try {
      if (transferAudioRef.current) {
        transferAudioRef.current.pause();
        transferAudioRef.current.src = '';
        transferAudioRef.current = null;
      }
    } catch (e) { /* ignore */ }

    // Capture callId BEFORE resetCallState clears it, so we can upload the
    // microphone recording for THIS call.
    const endedCallId = callState.callId;

    // Stop hold music if it's playing — use the hardened helper so the
    // primed element gets destroyed too (no zombie playback).
    try { stopHoldMusic(); } catch (e) { /* ignore */ }

    // Stop the local mic recorder and upload the leg in the background
    if (mediaRecorderRef.current && endedCallId) {
      stopAndUploadRecording(endedCallId).catch(() => {});
    }
    
    // Stop all polling
    if (answerPollingRef.current) {
      clearInterval(answerPollingRef.current);
      answerPollingRef.current = null;
    }
    if (icePollingRef.current) {
      clearInterval(icePollingRef.current);
      icePollingRef.current = null;
    }
    if (offerPollingRef.current) {
      clearInterval(offerPollingRef.current);
      offerPollingRef.current = null;
    }
    
    // Reset ICE polling flag
    hasStartedIcePollingRef.current = false;
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Stop local stream
    if (mediaState.localStream) {
      mediaState.localStream.getTracks().forEach(track => track.stop());
    }
    
    setCallState(prev => ({
      ...prev,
      status: CallStatus.ENDED
    }));
    
    // Reset after showing ended state
    setTimeout(() => resetCallState(), 2000);
  }, [mediaState.localStream, callState.callId, stopAndUploadRecording]);

  const resetCallState = useCallback(() => {
    // Hard-stop any audio that might be lingering. Belt-and-braces — every
    // path that resets state should also have stopped these explicitly, but
    // if something throws midway we still don't want the dial tone or hold
    // music to outlive the UI.
    try { stopRingback(); } catch (e) { /* ignore */ }
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    // Iron-clad hold-music kill (handles the primed element too).
    try { stopHoldMusic(); } catch (e) { /* ignore */ }
    try {
      if (transferAudioRef.current) {
        transferAudioRef.current.pause();
        transferAudioRef.current.src = '';
        transferAudioRef.current = null;
      }
    } catch (e) { /* ignore */ }
    // Tear down the secondary peer / mixer if anything is still alive.
    try { teardownSecondaryRef.current?.({ endBackend: false }); } catch (e) { /* ignore */ }

    // Stop any remaining streams
    if (mediaState.localStream) {
      mediaState.localStream.getTracks().forEach(track => track.stop());
    }
    
    setCallState({
      status: CallStatus.IDLE,
      callId: null,
      providerType: null,
      participants: [],
      isCaller: false,
      isMinimized: false,
      duration: 0,
      callMode: 'video',
      isOnHold: false,
      _heldByOther: false,
      _pendingWarmTransfer: null,
      _isInConference: false,
    });
    
    setMediaState({
      isMicOn: true,
      isCameraOn: true,
      localStream: null,
      remoteStream: null,
      secondaryRemoteStream: null,
    });
  }, [mediaState.localStream, stopHoldMusic]);

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // ============== Context Value ==============
  
  const value = {
    // State
    callState,
    mediaState,
    incomingCall,
    
    // Refs for video elements
    localVideoRef,
    remoteVideoRef,
    
    // Call lifecycle
    initiateCall,
    answerCall,
    declineCall,
    endCall,
    cancelCall,
    
    // Media controls
    toggleMic,
    toggleCamera,
    toggleMinimize,
    toggleHold,
    transferCall,
    completeWarmTransfer,
    cancelWarmTransfer,
    addParticipantToCall,
    secondaryRemoteVideoRef,

    // Voicemail flow (caller side, after no-answer ring timeout)
    recordAndSendVoicemail,
    stopVoicemailRecording,
    skipVoicemail,
    
    // Helpers
    formatDuration,
    
    // Status checks
    isInCall: callState.status !== CallStatus.IDLE && callState.status !== CallStatus.ENDED,
    isConnected: callState.status === CallStatus.CONNECTED,
    isCalling: callState.status === CallStatus.CALLING,
    hasIncomingCall: !!incomingCall
  };

  // Expose a global flag so non-React code (e.g. the auto-logout hook)
  // can short-circuit when a call is in progress.
  useEffect(() => {
    window.__orb_active_call = value.isInCall || value.hasIncomingCall;
    return () => { window.__orb_active_call = false; };
  }, [value.isInCall, value.hasIncomingCall]);

  return (
    <VideoCallContext.Provider value={value}>
      {children}
    </VideoCallContext.Provider>
  );
};

export default VideoCallContext;
